import type WaDialog from "@awesome.me/webawesome/dist/components/dialog/dialog.js";
import { consume } from "@lit/context";
import { ResizeController } from "@lit-labs/observers/resize-controller.js";
import { SignalWatcher, signal } from "@lit-labs/signals";
import { css, html, LitElement, nothing, type TemplateResult } from "lit";
import { query } from "lit/decorators/query.js";
import { customElement, property, queryAll, state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { cssWATweaks } from "../utils/css.ts";
import { equalSet } from "../utils/equal.ts";
import { puzzleContext } from "./contexts.ts";
import type { Puzzle } from "./puzzle.ts";
import type { ConfigDescription, ConfigItem, ConfigValues } from "./types.ts";

// Register components
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/checkbox/checkbox.js";
import "@awesome.me/webawesome/dist/components/dialog/dialog.js";
import "@awesome.me/webawesome/dist/components/divider/divider.js";
import "@awesome.me/webawesome/dist/components/input/input.js";
import "@awesome.me/webawesome/dist/components/option/option.js";
import "@awesome.me/webawesome/dist/components/scroller/scroller.js";
import "@awesome.me/webawesome/dist/components/select/select.js";
import "@awesome.me/webawesome/dist/components/radio/radio.js";
import "@awesome.me/webawesome/dist/components/radio-group/radio-group.js";

const isNumeric = (value: unknown) =>
  typeof value === "number" || (typeof value === "string" && /[0-9]+/.test(value));

interface PuzzleConfigChangeDetail {
  puzzle: Puzzle;
  changes: ConfigValues;
  value: ConfigValues;
}
export type PuzzleConfigChangeEvent = CustomEvent<PuzzleConfigChangeDetail>;

/**
 * Common code for configuration forms.
 * Must be used within a puzzle-context component.
 */
abstract class PuzzleConfigForm extends SignalWatcher(LitElement) {
  @consume({ context: puzzleContext, subscribe: true })
  @state()
  protected puzzle?: Puzzle;

  @property({ type: Boolean })
  autosubmit = false;

  @property({ type: Number, attribute: "choices-button-group-limit" })
  choicesButtonGroupLimit = 8;

  /**
   * The title for the dialog, per the config
   */
  get title(): string {
    return this._title.get();
  }

  protected _title = signal<string>("");

  @state()
  protected config?: ConfigDescription;

  @state()
  protected values: ConfigValues = {};

  @state()
  protected changes: ConfigValues = {};

  // Ids of choices sets where a horizontal radio-group fits.
  // If not, show a select menu instead.
  @state()
  private showAsButtonGroup = new Set<string>();

  @state()
  protected error?: string;

  protected abstract submitEventType: string;
  protected abstract getConfig(): Promise<ConfigDescription | undefined>;
  protected abstract getValues(): Promise<ConfigValues>;
  protected abstract setValues(values: ConfigValues): Promise<string | undefined>;

  protected async loadConfig(): Promise<void> {
    this.config = await this.getConfig();
    this._title.set(this.config?.title ?? "");
    await this.loadValues();
  }

  protected async loadValues() {
    this.values = await this.getValues();
    this.changes = {};
    this.error = undefined;
  }

  protected override async willUpdate(changedProperties: Map<string, unknown>) {
    if (changedProperties.has("puzzle") && this.puzzle) {
      await this.loadConfig();
    }
  }

  protected override updated(changedProperties: Map<string, unknown>) {
    if (
      changedProperties.has("config") ||
      changedProperties.has("choicesButtonGroupLimit")
    ) {
      this.updateShowAsButtonGroup();
    }
  }

  protected override render() {
    return html`
      <form part="form" @submit=${this.submit} ${this.resizeController.target(true)}>
        ${when(this.error, () => html`<div part="error">${this.error}</div>`)}

        ${Object.entries(this.config?.items ?? {}).map(([id, config]) => this.renderConfigItem(id, config))}
      </form>
    `;
  }

  private renderConfigItem(id: string, config: ConfigItem) {
    const value = this.changes[id] ?? this.values[id];
    // Abbreviating "%" or "%age" at the start of a label bugs me.
    // (E.g., Bridges. But I'm OK with "Expansion factor (%age)".)
    // Also improve "Size (s*s)": "(s&thinsp;&times;&thinsp;)" (e.g., Unequal).
    const label = config.name.replace(/^%/, "Percent").replace("s*s", "s × s");

    switch (config.type) {
      case "string":
        return html`
          <wa-input
            name=${id}
            inputmode=${isNumeric(value) ? "decimal" : "text"}
            label=${label}
            value=${value}
            @focus=${this.autoSelectInput}
            @change=${this.updateTextValue}
          ></wa-input>
        `;

      case "boolean":
        return html`
          <wa-checkbox 
            name=${id}
            ?checked=${value}
            @change=${this.updateCheckboxValue}
          >${label}</wa-checkbox>
        `;

      case "choices": {
        // Render choices as a select menu, or as a horizontal radio button group
        // for small numbers of choices if the button group fits one one line.
        // There's no way to know if it fits until it's rendered, so always render
        // both and use a resize observer to update showButtonGroup state so that
        // only one is visible.
        const showButtonGroup = this.showAsButtonGroup.has(id);
        const select = html`
          <wa-select
            name=${id}
            class=${showButtonGroup ? "hidden" : nothing}
            label=${label}
            value=${value}
            @change=${this.updateSelectValue}
          >
            ${config.choicenames.map(
              (choice, value) => html`
              <wa-option value=${value}>${choice}</wa-option>
            `,
            )}
          </wa-select>
        `;

        if (
          config.choicenames.length > this.choicesButtonGroupLimit &&
          !showButtonGroup
        ) {
          return select;
        }

        // Bind to .value (property) rather than value (attribute) to work
        // around a wa-radio-group bug where attribute changes aren't rendered.
        // https://github.com/shoelace-style/webawesome/issues/1273
        return html`
          <div class="choices">
            <wa-radio-group
              name=${id}
              class=${showButtonGroup ? nothing : "hidden"}
              label=${label}
              .value=${value}
              orientation="horizontal"
              @change=${this.updateSelectValue}
            >
              ${config.choicenames.map(
                (choice, value) => html`
                  <wa-radio value=${value} appearance="button">${choice}</wa-radio>
                `,
              )}
            </wa-radio-group>
            ${select}
          </div>
        `;
      }

      default:
        // @ts-expect-error: item.type never
        throw new Error(`Unknown config item type ${item.type}`);
    }
  }

  private resetFormItemValues() {
    // If the form has already been rendered, re-rendering with new value attributes
    // won't update input element state. Flush current values into item properties.
    for (const [id, { type }] of Object.entries(this.config?.items ?? [])) {
      const value = this.changes[id] ?? this.values[id];
      if (value !== undefined) {
        for (const element of this.shadowRoot?.querySelectorAll<HTMLInputElement>(
          `[name="${id}"]`,
        ) ?? []) {
          if (type === "boolean") {
            element.checked = Boolean(value);
          } else {
            element.value = String(value);
          }
        }
      }
    }
  }

  private autoSelectInput(event: FocusEvent) {
    const target = event.target as HTMLInputElement;
    target.select();
  }

  private async updateTextValue(event: CustomEvent) {
    const target = event.target as HTMLInputElement;
    this.changes[target.name] = target.value; // doesn't force redraw
    if (this.autosubmit) {
      await this.submit();
    }
  }

  private async updateCheckboxValue(event: CustomEvent) {
    const target = event.target as HTMLInputElement;
    this.changes[target.name] = target.checked; // doesn't force redraw
    if (this.autosubmit) {
      await this.submit();
    }
  }

  private async updateSelectValue(event: CustomEvent) {
    const target = event.target as HTMLInputElement;
    this.changes[target.name] = Number.parseInt(target.value, 10); // doesn't force redraw
    if (this.autosubmit) {
      await this.submit();
    }
  }

  public get hasErrors(): boolean {
    return this.error !== undefined;
  }

  public async submit(event?: Event) {
    event?.preventDefault();

    const result = await this.setValues(this.changes);
    if (result) {
      // If there's a result string, it's an error message
      this.error = result;
    } else {
      // Success
      this.error = undefined;
      if (this.puzzle) {
        this.dispatchEvent(
          new CustomEvent<PuzzleConfigChangeDetail>(this.submitEventType, {
            bubbles: true,
            composed: true,
            detail: {
              puzzle: this.puzzle,
              changes: this.changes,
              value: this.values,
            },
          }),
        );
      }

      this.values = { ...this.values, ...this.changes };
      this.changes = {};
    }
  }

  public async reset() {
    this.changes = {};
    this.error = undefined;
    this.resetFormItemValues();
  }

  public async reloadValues() {
    await this.loadValues();
    await this.updateComplete;
    this.resetFormItemValues();
  }

  //
  // showAsButtonGroup logic and resize observer
  //

  @queryAll("wa-radio-group")
  protected radioGroups?: HTMLElementTagNameMap["wa-radio-group"][];

  protected updateShowAsButtonGroup = (entries?: ResizeObserverEntry[]) => {
    // Update showButtonGroup state to indicate which
    // wa-radio-group elements fit without wrapping.
    const availableWidth = this.getFormContentWidth(entries);
    const idsThatFit = new Set<string>();
    for (const radioGroup of this.radioGroups ?? []) {
      const width = radioGroup.clientWidth;
      if (radioGroup.name && width > 0 && width <= availableWidth) {
        idsThatFit.add(radioGroup.name);
      }
    }
    if (!equalSet(this.showAsButtonGroup, idsThatFit)) {
      this.showAsButtonGroup = idsThatFit;
    }
  };

  protected resizeController = new ResizeController(this, {
    target: null, // Initialized to form during render
    callback: this.updateShowAsButtonGroup,
  });

  protected getFormContentWidth(entries?: ResizeObserverEntry[]): number {
    const form = this.shadowRoot?.querySelector('[part="form"]');
    if (!form) {
      return 0;
    }

    const formEntry = entries?.find((entry) => entry.target === form);
    if (formEntry) {
      return formEntry.contentRect.width;
    }

    const style = getComputedStyle(form);
    const inlinePadding =
      Number.parseFloat(style.paddingInlineStart) +
      Number.parseFloat(style.paddingInlineEnd);
    if (Number.isNaN(inlinePadding)) {
      console.warn(
        `Parse paddingInline failed: start='${style.paddingInlineStart}' end='${style.paddingInlineEnd}'`,
      );
      return 0; // uses wa-select, which should work at any width
    }
    return form.clientWidth - inlinePadding;
  }

  static styles = [
    cssWATweaks,
    css`
      :host {
        display: contents;
        --item-spacing: var(--wa-space-l);
      }
  
      [part="form"] {
        display: flex;
        flex-direction: column;
        gap: var(--item-spacing);
        align-items: flex-start;
      }
  
      [part="error"] {
        color: var(--wa-color-danger-on-normal);
        margin-bottom: var(--item-spacing);
      }
      
      .choices {
        position: relative;
        
        wa-radio-group {
          position: absolute;
          inset-block-start: 0;
          inset-inline-start: 0;
        }
      }
      .hidden {
        visibility: hidden;
      }

      /* Prevent radio-group and radio labels from wrapping */
      wa-radio-group::part(form-control-input) {
        flex-wrap: nowrap;
      }
      wa-radio::part(label) {
        white-space: nowrap;
      }
    `,
  ];
}

// PuzzleConfigForm calculates sizes that affect display after rendering
PuzzleConfigForm.disableWarning?.("change-in-update");

/**
 * Form for editing custom game params (custom puzzle type)
 */
@customElement("puzzle-custom-params-form")
export class PuzzleCustomParamsForm extends PuzzleConfigForm {
  protected override submitEventType = "puzzle-custom-params-change";

  protected override async getConfig() {
    return this.puzzle?.getCustomParamsConfig();
  }

  protected override async getValues() {
    return this.puzzle?.getCustomParams() ?? {};
  }

  protected override async setValues(values: ConfigValues) {
    return this.puzzle?.setCustomParams(values);
  }

  /**
   * Return encoded params for current form values
   */
  async getParams(): Promise<string | undefined> {
    if (this.puzzle) {
      const result = await this.puzzle.encodeCustomParams({
        ...this.values,
        ...this.changes,
      });
      if (!result.startsWith("#ERROR:")) {
        return result;
      }
      console.warn(`PuzzleCustomParamsForm.getParams: ${result}`);
    }
  }
}

/**
 * Form for editing puzzle preferences
 */
@customElement("puzzle-preferences-form")
export class PuzzlePreferencesForm extends PuzzleConfigForm {
  protected override submitEventType = "puzzle-preferences-change";

  protected override async getConfig() {
    return this.puzzle?.getPreferencesConfig();
  }

  protected override async getValues() {
    return this.puzzle?.getPreferences() ?? {};
  }

  protected override async setValues(values: ConfigValues) {
    const result = await this.puzzle?.setPreferences(values);
    if (!result) {
      await this.puzzle?.redraw();
    }
    return result;
  }
}

abstract class PuzzleConfigDialog extends SignalWatcher(LitElement) {
  /**
   * The label for the submit button
   */
  @property({ type: String, attribute: "submit-label" })
  submitLabel = "OK";

  /**
   * The label for the cancel button
   */
  @property({ type: String, attribute: "cancel-label" })
  cancelLabel = "Cancel";

  @property({ type: String, attribute: "dialog-title" })
  dialogTitle = "Configuration";

  @query("wa-dialog", true)
  protected dialog?: WaDialog;

  protected abstract form?: PuzzleConfigForm;

  protected override render() {
    return html`
      <wa-dialog label=${this.dialogTitle}>
        <wa-scroller orientation="vertical">
          ${this.renderConfigForm()}
        </wa-scroller>
        
        <div slot="footer" part="footer">
          <wa-button @click=${this.handleCancel}>${this.cancelLabel}</wa-button>
          <wa-button variant="brand" @click=${this.handleSubmit}>${this.submitLabel}</wa-button>
        </div>
      </wa-dialog>
    `;
  }

  protected override updated() {
    if (!this.hasAttribute("dialog-title")) {
      // Get the dialog title from the form.
      // This causes Lit changed-in-update warning.
      const title = this.form?.title;
      if (title && title !== this.dialogTitle) {
        this.dialogTitle = title;
      }
    }
  }

  protected abstract renderConfigForm(): TemplateResult;

  protected async handleSubmit() {
    await this.form?.submit();
    if (!this.form?.hasErrors) {
      await this.hide();
    }
  }

  protected async handleCancel() {
    await this.form?.reset();
    await this.hide();
  }

  // Expose dialog's open property
  get open() {
    return this.dialog?.open ?? false;
  }
  set open(isOpen: boolean) {
    if (this.dialog) {
      this.dialog.open = isOpen;
    }
  }

  show() {
    this.open = true;
  }

  hide() {
    this.open = false;
  }

  async reloadValues(): Promise<void> {
    return this.form?.reloadValues();
  }

  static styles = [
    cssWATweaks,
    css`
      :host {
        display: contents;
      }
      
      wa-dialog::part(body) {
        display: flex;
        flex-direction: column;
        
        /* Move overflow scrolling to wa-scroller; constrain size */
        /* Move half of padding to form to avoid clipping focus rings */
        --padding: var(--wa-space-l);
        padding: calc(var(--padding) / 2);
      }
      
      wa-scroller {
        min-height: 1em; /* flex to body size */
        
        /* Make the shadow visibly larger than puzzle-config-form --item-spacing, 
           but leave at least enough room for a full form control between shadows. */
        --shadow-size: min(
            calc(2.5 * var(--wa-space-l)),
            calc((100% - var(--wa-form-control-height)) / 2) 
        );
        
        /* Try to avoid shadowing focused item during keyboard nav. */
        &::part(content) {
          scroll-padding-block: var(--shadow-size);
        }
      }
      
      [part="form"]::part(form) {
        /* Ensure focus rings don't get clipped.
         * Related: https://github.com/shoelace-style/webawesome/discussions/1459 */
        padding: calc(var(--padding) / 2);
      }
  
      [part="footer"] {
        display: grid;
        grid-auto-flow: column;
        grid-auto-columns: 1fr;
        justify-content: end;
        align-items: center;
        gap: var(--wa-space-s);
      }
    `,
  ];
}
// change-in-update is necessary because title is retrieved
// from PuzzleConfigForm after first render.
PuzzleConfigDialog.disableWarning?.("change-in-update");

/**
 * Dialog for editing custom game params (custom puzzle type)
 */
@customElement("puzzle-custom-params-dialog")
export class PuzzleCustomParamsDialog extends PuzzleConfigDialog {
  @query("puzzle-custom-params-form")
  protected form?: PuzzleCustomParamsForm;

  protected override renderConfigForm() {
    return html`
      <puzzle-custom-params-form part="form"></puzzle-custom-params-form>
    `;
  }

  async getParams(): Promise<string | undefined> {
    return this.form?.getParams();
  }
}

/**
 * Dialog for editing puzzle preferences
 */
@customElement("puzzle-preferences-dialog")
export class PuzzlePreferencesDialog extends PuzzleConfigDialog {
  @query("puzzle-preferences-form")
  protected form?: PuzzlePreferencesForm;

  protected override renderConfigForm() {
    return html`
      <puzzle-preferences-form part="form"></puzzle-preferences-form>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "puzzle-custom-params-form": PuzzleCustomParamsForm;
    "puzzle-preferences-form": PuzzlePreferencesForm;
    "puzzle-custom-params-dialog": PuzzleCustomParamsDialog;
    "puzzle-preferences-dialog": PuzzlePreferencesDialog;
  }

  interface HTMLElementEventMap {
    "puzzle-custom-params-change": PuzzleConfigChangeEvent;
    "puzzle-preferences-change": PuzzleConfigChangeEvent;
  }
}
